import { useState } from 'react'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import { IAPISimulationItem, IAPISimulationItemRun } from 'extra/types/ApiModels'
import { ChatHistoryComponent } from '../../components/DataDisplayComponents'

export function SimulationConversationViewer ({
  item,
  targetId,
  initialRunIndex,
  onClose,
}: {
  item: IAPISimulationItem
  targetId: string
  initialRunIndex: number
  onClose: () => void
}) {
  const runs: Array<IAPISimulationItemRun> = (item.runs ?? [])
    .filter(r => r.targetId === targetId)
    .sort((a, b) => a.runIndex - b.runIndex)

  const [runIndex, setRunIndex] = useState(initialRunIndex)
  const currentRun = runs[runIndex]
  const runResult = currentRun?.result
  const evalResult = currentRun?.evalResult

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-lg w-full max-w-6xl max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-medium">
              Conversation - {item.title || item.id.slice(-8)}
            </h3>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <button
                onClick={() => setRunIndex(Math.max(0, runIndex - 1))}
                disabled={runIndex === 0}
                className="p-1 hover:text-white disabled:opacity-30 cursor-pointer"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span>Run {runIndex + 1} / {runs.length}</span>
              <button
                onClick={() => setRunIndex(Math.min(runs.length - 1, runIndex + 1))}
                disabled={runIndex >= runs.length - 1}
                className="p-1 hover:text-white disabled:opacity-30 cursor-pointer"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white cursor-pointer">
            <X className="w-5 h-5" />
          </button>
        </div>

        {runResult
          ? (
          <div className="flex-1 min-h-0 overflow-y-auto p-4">
            <div className="flex gap-4 text-sm text-gray-400 mb-4">
              <span>Status: <span className={runResult.status === 'completed' ? 'text-green-400' : 'text-red-400'}>{runResult.status}</span></span>
              <span>Turns: {runResult.turnCount}</span>
              <span>Agent Latency: {((currentRun?.metrics?.latency ?? 0) / 1000).toFixed(1)}s</span>
              <span>Agent tokens: {runResult.agentTokenUsage.inputTokens + runResult.agentTokenUsage.outputTokens}</span>
              <span>Driver tokens: {runResult.driverTokenUsage.inputTokens + runResult.driverTokenUsage.outputTokens}</span>
            </div>

            {evalResult && (
              <div className="mb-4 p-3 rounded border border-gray-700 bg-gray-800/50">
                <div className="text-sm font-medium mb-1">
                  Eval Score: <span style={{ color: `hsl(${evalResult.score * 120}, 80%, 60%)` }}>
                    {evalResult.score.toFixed(2)}
                  </span>
                </div>
                {evalResult.feedback && (
                  <pre className="text-xs text-gray-400 whitespace-pre-wrap">{evalResult.feedback}</pre>
                )}
              </div>
            )}

            {runResult.error && (
              <div className="alert-error mb-4">{runResult.error}</div>
            )}

            <ChatHistoryComponent history={runResult.conversation} expand />
          </div>
            )
          : (
          <div className="p-4 text-gray-400">No data for this run.</div>
            )}
      </div>
    </div>
  )
}
